import { useEffect, useState } from 'react';
import type { SyncUser, SyncUserType } from '../../types';

export const TYPES: SyncUserType[] = ['admin', 'brand_admin', 'creator'];

export const TYPE_LABEL: Record<SyncUserType, string> = {
  admin: 'GV admins',
  brand_admin: 'Brand admins',
  creator: 'Creators',
};

export const TYPE_SINGULAR: Record<SyncUserType, string> = {
  admin: 'GV admin',
  brand_admin: 'Brand admin',
  creator: 'Creator',
};

/** Ids are only unique within a type, so the selection is keyed by both. */
export const userKey = (user: Pick<SyncUser, 'type' | 'id'>) => `${user.type}:${user.id}`;

export type Selection = Record<string, SyncUser>;

export const selectionByType = (selection: Selection): Record<SyncUserType, SyncUser[]> => {
  const grouped: Record<SyncUserType, SyncUser[]> = { admin: [], brand_admin: [], creator: [] };
  Object.values(selection).forEach((user) => grouped[user.type].push(user));
  return grouped;
};

export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
}

/** Splits pasted text on commas, semicolons and whitespace into unique lowercase emails. */
export const parseEmails = (text: string): string[] => {
  const emails = text
    .split(/[\s,;]+/)
    .map((part) => part.trim().toLowerCase())
    .filter((part) => part.includes('@'));

  return Array.from(new Set(emails));
};
